import { useParams, Link, Navigate } from "react-router-dom";
import { Phone, CheckCircle2, ArrowRight } from "lucide-react";
import { SERVICES, NAP, BOOK_URL, SITE_URL } from "../lib/data";
import { TID } from "../lib/testIds";
import CallToActionStrip from "../components/CallToActionStrip";
import FAQ from "../components/FAQ";
import { useSEO } from "../lib/useSEO";
import { trackBookClick } from "../lib/analytics";
import { buildBreadcrumbs } from "../lib/breadcrumbs";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);
  useSEO({
    title: service ? `${service.short} in Chicago — Act Fast Appliance Repair` : "Service — Act Fast Appliance Repair",
    description: service
      ? `${service.description} Same-day ${service.name.toLowerCase()} repair across Chicagoland with a full parts & labor warranty.`
      : "Appliance repair services",
    path: `/services/${slug || ""}`,
    jsonLd: service ? [
      {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": service.short,
        "serviceType": service.short,
        "description": service.description,
        "areaServed": { "@type": "City", "name": "Chicago" },
        "provider": {
          "@type": "LocalBusiness",
          "name": "Act Fast Appliance Repair",
          "telephone": NAP.phoneDisplay,
          "url": `${SITE_URL}/services/${service.slug}`,
          "address": {
            "@type": "PostalAddress",
            "streetAddress": NAP.addressLine1,
            "addressLocality": NAP.city,
            "addressRegion": NAP.state,
            "postalCode": NAP.zip,
            "addressCountry": "US"
          }
        },
        "brand": service.brands.map((b) => ({ "@type": "Brand", "name": b }))
      },
      buildBreadcrumbs([
        { name: "Home", path: "/" },
        { name: "Services", path: "/services" },
        { name: service.short, path: `/services/${service.slug}` },
      ]),
    ] : undefined,
  });
  if (!service) return <Navigate to="/services" replace />;
  const Icon = service.icon;

  return (
    <div className="bg-black">
      <section className="relative pt-32 pb-12 lg:pt-44 lg:pb-20 border-b border-[#262626]">
        <div className="absolute inset-0 af-grid-noise opacity-40" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
          <Link to="/services" className="text-xs text-neutral-500 uppercase tracking-widest hover:text-[#DC2626] mb-6 inline-block">
            ← Back to Services
          </Link>
          <div className="flex items-start gap-5 mb-6">
            <div className="w-16 h-16 flex items-center justify-center bg-[#0a0a0a] border border-[#DC2626] shrink-0">
              <Icon size={32} className="text-[#DC2626]" />
            </div>
            <div>
              <p className="af-eyebrow mb-2">Service</p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold font-display leading-[1.0]">
                {service.short.split(" ").slice(0, -1).join(" ")}{" "}
                <span className="text-[#DC2626]">{service.short.split(" ").slice(-1)}</span>
              </h1>
            </div>
          </div>
          <p className="text-lg text-neutral-300 max-w-3xl leading-relaxed">{service.description}</p>
        </div>
      </section>

      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <p className="af-eyebrow mb-3">Common Problems We Fix</p>
            <h2 className="text-3xl sm:text-4xl font-bold font-display mb-8">
              Typical {service.name} Issues
            </h2>
            <ul className="space-y-3 mb-12">
              {service.pains.map((p) => (
                <li key={p} className="flex items-start gap-3 p-4 border border-[#262626] bg-[#0a0a0a]">
                  <CheckCircle2 size={20} className="text-[#DC2626] shrink-0 mt-0.5" />
                  <span className="text-white">{p}</span>
                </li>
              ))}
            </ul>

            <p className="af-eyebrow mb-3">Brands We Service</p>
            <h2 className="text-3xl sm:text-4xl font-bold font-display mb-6">
              Factory-Trained On The Full Lineup
            </h2>
            <div className="flex flex-wrap gap-2 mb-12">
              {service.brands.map((b) => (
                <span key={b} className="af-pill text-sm">{b}</span>
              ))}
            </div>

            <p className="af-eyebrow mb-3">Upfront Pricing Guarantee</p>
            <h2 className="text-3xl sm:text-4xl font-bold font-display mb-4">
              <span className="text-[#DC2626]">No Surprises</span>. Ever.
            </h2>
            <p className="text-neutral-300 leading-relaxed mb-3">
              {service.pricingNote} You always get a written estimate before any work begins, and every
              repair is backed by our signature parts and labor warranty.
            </p>
          </div>

          <aside className="lg:col-span-5">
            <div className="sticky top-28 af-card p-7 lg:p-8">
              <p className="af-eyebrow mb-3">Book This Repair</p>
              <h3 className="text-2xl font-bold font-display mb-4">
                Same-Day {service.name} Service
              </h3>
              <p className="text-neutral-400 text-sm mb-6 leading-relaxed">
                Call us before noon and a factory-trained Act Fast technician is usually at your door the
                same afternoon. Online booking open 24/7.
              </p>
              <a href={NAP.phoneTel} className="af-btn-primary w-full justify-center mb-3">
                <Phone size={18} /> {NAP.phoneDisplay}
              </a>
              <a
                href={BOOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackBookClick(`service-${service.slug}`)}
                className="af-btn-outline w-full justify-center"
                data-testid="service-book-online"
              >
                Book Online <ArrowRight size={16} />
              </a>
              <div className="mt-6 pt-6 border-t border-[#262626] text-sm text-neutral-400 space-y-2">
                <div>✓ Parts & labor warranty</div>
                <div>✓ Licensed & insured</div>
                <div>✓ Up-front pricing</div>
                <div>✓ Parts on the truck</div>
              </div>
            </div>
          </aside>
        </div>
      </section>

      <CallToActionStrip />
      <FAQ />
    </div>
  );
}
